import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  TrendingUp, TrendingDown, Minus, IndianRupee, BedDouble,
  Users, Lightbulb, AlertTriangle, CheckCircle2, Stethoscope,
  ArrowUpRight,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { dashboardApi } from '@/lib/api';
import type { OwnerDashboardData, OwnerInsight } from './types';
import { DashboardShell, LoadingState, ErrorState } from './_shared';

// ── Formatters ───────────────────────────────────────────────────────────────
function lakh(n: number)     { return `₹${(n / 1_00_000).toFixed(2)}L`; }
function shortInr(n: number) {
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)    return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n}`;
}

// ── Sub-components ───────────────────────────────────────────────────────────
interface KpiCardProps {
  label:   string;
  value:   string;
  trend?:  number;
  sub?:    string;
  accent?: string;
  icon:    React.ElementType;
}

function KpiCard({ label, value, trend, sub, accent = 'bg-orange/15', icon: Icon }: KpiCardProps) {
  const up   = (trend ?? 0) > 0;
  const flat = trend === 0 || trend === undefined;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', accent)}>
          <Icon size={16} className="text-navy"/>
        </div>
        {trend !== undefined && (
          <span className={clsx(
            'flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full',
            flat  ? 'bg-gray-100 text-gray-500'      :
            up    ? 'bg-emerald-50 text-emerald-700' :
                    'bg-red-50 text-red-600',
          )}>
            {flat ? <Minus size={10}/> : up ? <TrendingUp size={10}/> : <TrendingDown size={10}/>}
            {flat ? '0%' : `${up ? '+' : ''}${trend}%`}
          </span>
        )}
      </div>
      <div>
        <div className="text-2xl font-bold text-navy leading-tight">{value}</div>
        <div className="text-xs text-gray-400 mt-0.5">{label}</div>
      </div>
      {sub && <div className="text-xs text-gray-500 border-t border-gray-100 pt-2">{sub}</div>}
    </div>
  );
}

const INSIGHT_STYLE: Record<OwnerInsight['type'], { bg: string; border: string; icon: React.ElementType; iconCls: string }> = {
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2,  iconCls: 'text-emerald-600' },
  warning: { bg: 'bg-amber-50',   border: 'border-amber-200',   icon: AlertTriangle, iconCls: 'text-amber-600'   },
  danger:  { bg: 'bg-red-50',     border: 'border-red-200',     icon: AlertTriangle, iconCls: 'text-red-600'     },
  info:    { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: Lightbulb,     iconCls: 'text-blue-600'    },
};

function InsightCard({ insight }: { insight: OwnerInsight }) {
  const s = INSIGHT_STYLE[insight.type];
  const InsightIcon = s.icon;
  return (
    <div className={clsx('rounded-xl border p-3.5 flex gap-3', s.bg, s.border)}>
      <InsightIcon size={15} className={clsx('shrink-0 mt-0.5', s.iconCls)}/>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-navy leading-snug">{insight.title}</p>
        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{insight.detail}</p>
        {insight.action && (
          <button className="mt-1.5 text-xs text-navy font-semibold flex items-center gap-0.5 hover:text-orange transition">
            {insight.action} <ArrowUpRight size={11}/>
          </button>
        )}
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, currency }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-semibold text-navy mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }}/>
          <span className="text-gray-500">{p.name}:</span>
          <span className="font-semibold text-navy">{currency ? shortInr(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function StarRating({ score }: { score: number }) {
  return (
    <span className={clsx(
      'text-xs font-bold',
      score >= 4.7 ? 'text-emerald-600' : score >= 4.4 ? 'text-amber-600' : 'text-red-500',
    )}>
      ★ {score.toFixed(1)}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function OwnerDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<OwnerDashboardData>({
    queryKey:        ['dashboard-owner'],
    queryFn:         () => dashboardApi.owner().then(r => r.data),
    staleTime:       5 * 60_000,
    refetchInterval: 10 * 60_000,
  });

  const [activeRevTab, setActiveRevTab] = useState<'trend' | 'mix'>('trend');

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  const { kpi, revenueTrend, revenueMix, deptRevenue, collectionsVsBilling, doctors, patientFunnel, insights } = data;
  const totalMtdRevenue = deptRevenue.reduce((s, d) => s + d.revenue, 0) || 1;
  const sortedDoctors   = [...doctors].sort((a, b) => b.revenue - a.revenue);
  const maxOpd          = Math.max(1, ...sortedDoctors.map(d => d.opdCount));

  return (
    <DashboardShell
      title="Hospital Intelligence Dashboard"
      subtitle={`Owner view · ${new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      {/* ── KPI Strip ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard icon={IndianRupee} label="Revenue Today"          value={shortInr(kpi.todayRevenue)}     trend={kpi.todayRevenueTrend}  accent="bg-orange/15"/>
        <KpiCard icon={TrendingUp}  label="Revenue MTD"            value={lakh(kpi.mtdRevenue)}           trend={kpi.mtdRevenueTrend}    accent="bg-blue-50"/>
        <KpiCard icon={IndianRupee} label="Revenue YTD"            value={lakh(kpi.ytdRevenue)}           trend={kpi.ytdRevenueTrend}    accent="bg-violet-50"/>
        <KpiCard
          icon={BedDouble}
          label="Bed Occupancy"
          value={`${kpi.occupiedBeds}/${kpi.totalBeds}`}
          sub={`${kpi.bedOccupancy}% occupied`}
          accent="bg-emerald-50"
        />
        <KpiCard icon={Users}       label="OPD Patients Today"     value={String(kpi.opdToday)}           trend={kpi.opdTrend}           accent="bg-amber-50"/>
        <KpiCard
          icon={IndianRupee}
          label="Pending Receivables"
          value={shortInr(kpi.pendingReceivables)}
          trend={kpi.pendingTrend}
          accent="bg-red-50"
        />
      </div>

      {/* ── Revenue Charts ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-navy">Revenue Analysis</h2>
          <div className="flex gap-1">
            {(['trend', 'mix'] as const).map(t => (
              <button
                key={t}
                onClick={() => setActiveRevTab(t)}
                className={clsx(
                  'px-3 py-1 text-xs font-semibold rounded-lg transition',
                  activeRevTab === t ? 'bg-navy text-white' : 'text-gray-400 hover:text-navy',
                )}>
                {t === 'trend' ? '13-Day Trend' : 'Department Mix'}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 py-4">
          {activeRevTab === 'trend' ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={revenueTrend}>
                <defs>
                  <linearGradient id="gIpd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#1a1a2e" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#1a1a2e" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gOpd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#E8A020" stopOpacity={0.18}/>
                    <stop offset="95%" stopColor="#E8A020" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={shortInr} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={52}/>
                <Tooltip content={<ChartTooltip currency/>}/>
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: '#6b7280' }}/>
                <Area dataKey="ipd"      name="IPD"      type="monotone" stroke="#1a1a2e" strokeWidth={2} fill="url(#gIpd)"/>
                <Area dataKey="opd"      name="OPD"      type="monotone" stroke="#E8A020" strokeWidth={2} fill="url(#gOpd)"/>
                <Area dataKey="pharmacy" name="Pharmacy" type="monotone" stroke="#3b82f6" strokeWidth={1.5} fill="none" strokeDasharray="4 2"/>
                <Area dataKey="lab"      name="Lab"      type="monotone" stroke="#10b981" strokeWidth={1.5} fill="none" strokeDasharray="4 2"/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center gap-8">
              <ResponsiveContainer width="40%" height={200}>
                <PieChart>
                  <Pie
                    data={revenueMix}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={3}
                    dataKey="value">
                    {revenueMix.map((entry, i) => (
                      <Cell key={i} fill={entry.color}/>
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => shortInr(v)}/>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {revenueMix.map(m => (
                  <div key={m.name} className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: m.color }}/>
                    <span className="text-sm text-gray-600 flex-1">{m.name}</span>
                    <span className="text-sm font-semibold text-navy">{shortInr(m.value)}</span>
                    <span className="text-xs text-gray-400 w-10 text-right">
                      {Math.round((m.value / totalMtdRevenue) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Middle Row: Billing vs Collection + Smart Insights ──────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        {/* Billed vs Collected */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card">
          <div className="px-5 py-3.5 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-navy">Billed vs Collected — 7 Months</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Current collection efficiency:{' '}
              <strong className={clsx(kpi.collectionEfficiency >= 85 ? 'text-emerald-600' : 'text-amber-600')}>
                {kpi.collectionEfficiency}%
              </strong>
            </p>
          </div>
          <div className="px-5 py-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={collectionsVsBilling} barSize={14} barGap={3}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={shortInr} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={52}/>
                <Tooltip content={<ChartTooltip currency/>}/>
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: '#6b7280' }}/>
                <Bar dataKey="billed"    name="Billed"    fill="#1a1a2e" radius={[3,3,0,0]}/>
                <Bar dataKey="collected" name="Collected" fill="#E8A020" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Smart Insights */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100">
            <Lightbulb size={14} className="text-orange"/>
            <h2 className="text-sm font-semibold text-navy">Smart Insights</h2>
            <span className="ml-auto text-2xs text-gray-400">Auto-generated · updated hourly</span>
          </div>
          <div className="p-4 space-y-2.5 overflow-y-auto max-h-[280px]">
            {insights.map((ins, i) => <InsightCard key={i} insight={ins}/>)}
          </div>
        </div>
      </div>

      {/* ── Doctor Performance ────────────────────────────────────────────────── */}
      {sortedDoctors.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Stethoscope size={14} className="text-orange"/>
              <h2 className="text-sm font-semibold text-navy">Doctor Performance · This Month</h2>
            </div>
            <span className="text-xs text-gray-400">OPD patients · revenue · satisfaction</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr>
                  {['#', 'Doctor', 'Specialty', 'OPD Patients', 'Revenue (MTD)', 'New Patients', 'Avg Consult', 'Satisfaction'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDoctors.map((d, idx) => (
                  <tr key={d.name} className="hover:bg-gray-50 transition border-t border-gray-50">
                    <td className="px-4 py-3">
                      <span className={clsx(
                        'w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center',
                        idx === 0 ? 'bg-orange text-white' : 'bg-gray-100 text-gray-400',
                      )}>
                        {idx + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-navy">{d.name}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{d.specialty}</td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-navy">{d.opdCount}</span>
                      <div className="w-24 h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-orange rounded-full" style={{ width: `${Math.round((d.opdCount / maxOpd) * 100)}%` }}/>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-navy">{shortInr(d.revenue)}</td>
                    <td className="px-4 py-3 text-gray-500">{d.newPatients}</td>
                    <td className="px-4 py-3 text-gray-500">{d.avgConsultMin} min</td>
                    <td className="px-4 py-3"><StarRating score={d.satisfaction}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Bottom Row: Department P&L + Patient Funnel ───────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        {/* Department P&L */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-navy">Department P&amp;L · MTD</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="px-4 py-2.5 text-left  text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Department</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Revenue</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Cost</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Margin</th>
                  <th className="px-4 py-2.5 text-left  text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Contribution</th>
                </tr>
              </thead>
              <tbody>
                {deptRevenue.map(d => {
                  const margin = d.revenue > 0 ? Math.round(((d.revenue - d.cost) / d.revenue) * 100) : 0;
                  const share  = Math.round((d.revenue / totalMtdRevenue) * 100);
                  return (
                    <tr key={d.dept} className="hover:bg-gray-50 border-t border-gray-50">
                      <td className="px-4 py-3 font-medium text-navy">{d.dept}</td>
                      <td className="px-4 py-3 text-right font-semibold text-navy">{shortInr(d.revenue)}</td>
                      <td className="px-4 py-3 text-right text-gray-400">{shortInr(d.cost)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={clsx('font-semibold text-xs', margin >= 50 ? 'text-emerald-600' : margin >= 30 ? 'text-amber-600' : 'text-red-500')}>
                          {margin}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-navy rounded-full" style={{ width: `${share}%` }}/>
                          </div>
                          <span className="text-2xs text-gray-400 w-8">{share}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Patient Journey Funnel */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100">
            <Users size={14} className="text-orange"/>
            <h2 className="text-sm font-semibold text-navy">Patient Journey Funnel · This Month</h2>
          </div>
          <div className="px-5 py-5 space-y-3">
            {patientFunnel.map((stage, i) => (
              <div key={stage.stage}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">{stage.stage}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-navy">{stage.count.toLocaleString()}</span>
                    <span className="text-xs text-gray-400 w-10 text-right">{stage.pct}%</span>
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width:      `${stage.pct}%`,
                      background: i === 0 ? '#1a1a2e' : `hsl(${220 - i * 30}, 70%, ${40 + i * 8}%)`,
                    }}
                  />
                </div>
                {i < patientFunnel.length - 1 && patientFunnel[i + 1].count > 0 && stage.count > 0 && (
                  <p className="text-2xs text-gray-400 mt-0.5 text-right">
                    {Math.round((patientFunnel[i + 1].count / stage.count) * 100)}% proceed to next step
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

    </DashboardShell>
  );
}
